import graphql__8c503fae6e09788c5043 from "./__generated__/EditSupplierAmountOverrideDialogSuppliersQuery.graphql.ts";
import graphql__36110ca0faa2674b015f from "./__generated__/EditSupplierAmountOverrideDialogMutation.graphql.ts";import * as React from "react";
import {useMemo} from "react";
import {fetchQuery, graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import * as yup from "yup";
import FormDialog from "./FormDialog";
import Section from "../form/Section";
import Row from "../form/Row";
import Label from "../form/Label";
import Field from "../form/Field";
import asFormikField from "../abstract/asFormikField";
import AbstractCombobox from "../abstract/AbstractCombobox";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {formatCentsForInput, parseAmountToCents, yupAmount} from "../../helper/amountInput";
import {useErrorContext} from "../../contexts/ErrorContext";
import type {EditSupplierAmountOverrideDialogMutation} from "./__generated__/EditSupplierAmountOverrideDialogMutation.graphql";

type SupplierEntry = {
    id: string
    name: string
    billingCycle: string
}

type Props = {
    // Without an override, the dialog sets one for a supplier picked in the dialog
    override?: {
        id: string
        netAmount: number
        note?: string | null
        supplier?: {
            name: string
        } | null
    }
    onClose: (changed: boolean) => void
}

// Only these suppliers are continued in the expense forecast
const REGULAR_BILLING_CYCLES = ['MONTHLY_IN_ARREARS', 'MONTHLY_IN_ADVANCE', 'QUARTERLY_IN_ADVANCE', 'YEARLY_IN_ADVANCE'];

/**
 * Loads the suppliers once and filters them by the search term in the browser.
 */
const suppliersDataProvider = (relayEnvironment) => {
    let suppliers: Promise<SupplierEntry[]> | null = null;
    return (searchTerm: string = ''): Promise<SupplierEntry[]> => {
        const loaded = suppliers ??= fetchQuery(relayEnvironment, graphql__8c503fae6e09788c5043, {}).toPromise().then((data: any) => (data?.suppliers ?? [])
            .filter(supplier => REGULAR_BILLING_CYCLES.includes(supplier.billingCycle))
            .map(supplier => ({id: supplier.id, name: supplier.name, billingCycle: supplier.billingCycle}))
            .sort((a, b) => a.name.localeCompare(b.name)));
        const term = searchTerm.trim().toLowerCase();
        return loaded.then(entries => entries.filter(entry => term === '' || entry.name.toLowerCase().includes(term)));
    };
};

const SupplierCombobox = asFormikField(AbstractCombobox, 'supplier');

const setMutation = graphql__36110ca0faa2674b015f;

/**
 * Sets the net amount per invoice the expense forecast uses for a supplier. Only suppliers which
 * bill regularly can be picked, and the supplier cannot be changed for an existing amount.
 */
const EditSupplierAmountOverrideDialog: React.FC<Props> = ({override, onClose}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const dataProvider = useMemo(() => suppliersDataProvider(relayEnvironment), [relayEnvironment]);

    const handleSubmission = (values) => {
        commitMutationWithErrorHandling<EditSupplierAmountOverrideDialogMutation>(
            relayEnvironment,
            {
                mutation: setMutation,
                variables: {
                    input: {
                        id: override ? override.id : values.supplier.id,
                        netAmount: parseAmountToCents(values.netAmount) as number,
                        note: values.note.trim() === '' ? null : values.note.trim(),
                    },
                },
                onCompleted: (_, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                    onClose(true);
                },
                onError: (error) => {
                    console.error(error);
                    onClose(false);
                },
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToSetSupplierAmountOverride'})}
        );
    };

    const validationSchema = yup.object().shape({
        ...(override ? {} : {supplier: yup.object().required(intl.formatMessage({id: 'validation.field.required'}))}),
        netAmount: yupAmount(intl, {nonNegative: true}),
        note: yup.string().max(255, intl.formatMessage({id: 'validation.field.noteTooLong'})),
    });

    return (
        <FormDialog handleCloseDialog={() => onClose(false)}
                    handleSubmission={handleSubmission}
                    initialValues={{
                        netAmount: override ? formatCentsForInput(override.netAmount) : '',
                        note: override?.note ?? '',
                    }}
                    validationSchema={validationSchema}
                    headline={intl.formatMessage({id: override ? 'dialog.EditSupplierAmountOverrideDialog.editHeadline' : 'dialog.EditSupplierAmountOverrideDialog.addHeadline'})}
                    submitLabel={intl.formatMessage({id: 'button.save'})}>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditSupplierAmountOverrideDialog.supplierLabel'})}</Label>
                    {override ? (
                        <p className="text-sm text-gray-900">{override.supplier?.name ?? override.id}</p>
                    ) : (
                        <SupplierCombobox
                            getEntryId={(entry: SupplierEntry) => entry.id}
                            getEntryLabel={(entry: SupplierEntry | null) => entry ? entry.name : ''}
                            getEntrySecondaryLabel={(entry: SupplierEntry | null) => entry ? intl.formatMessage({id: 'expense.billingCycle.' + entry.billingCycle}) : undefined}
                            dataProvider={dataProvider}
                            autoFocus={true}
                        />
                    )}
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditSupplierAmountOverrideDialog.netAmountLabel'})}</Label>
                    <Field name="netAmount" type="text" inputMode="decimal" placeholder="0,00" autoFocus={override !== undefined} data-1p-ignore="true"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditSupplierAmountOverrideDialog.noteLabel'})}</Label>
                    <Field name="note" type="text" data-1p-ignore="true"/>
                </Row>
                <p className="text-sm text-gray-500">
                    {intl.formatMessage({id: 'dialog.EditSupplierAmountOverrideDialog.description'})}
                </p>
            </Section>
        </FormDialog>
    );
};

export default EditSupplierAmountOverrideDialog;
